import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import lawFirmsService from '../../services/api/lawFirmsService';

export const LAW_FIRMS_KEY = ['lawFirms'];
export const CURRENT_FIRM_KEY = ['lawFirms', 'current'];

/**
 * Hook to fetch current user's law firm info
 * Used for access control and determining permissions
 */
export function useCurrentFirm() {
  return useQuery({
    queryKey: CURRENT_FIRM_KEY,
    queryFn: () => lawFirmsService.getCurrentFirm(),
    staleTime: 1000 * 60 * 5, // 5 minutes - firm info rarely changes
    refetchOnWindowFocus: false
  });
}

/**
 * Hook to fetch all law firms (admin only)
 */
export function useLawFirms(options = {}) {
  return useQuery({
    queryKey: [...LAW_FIRMS_KEY, 'all', options],
    queryFn: () => lawFirmsService.getAllFirms(options),
    staleTime: 1000 * 60,
    enabled: options.enabled !== false
  });
}

/**
 * Hook to fetch external law firms (for transfer dropdown)
 */
export function useExternalFirms(options = {}) {
  return useQuery({
    queryKey: [...LAW_FIRMS_KEY, 'external'],
    queryFn: () => lawFirmsService.getExternalFirms(),
    staleTime: 1000 * 60,
    enabled: options.enabled !== false
  });
}

/**
 * Hook to create a law firm
 */
export function useCreateFirm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => lawFirmsService.createFirm(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LAW_FIRMS_KEY });
    }
  });
}

/**
 * Hook to update a law firm
 */
export function useUpdateFirm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => lawFirmsService.updateFirm(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LAW_FIRMS_KEY });
    }
  });
}

/**
 * Hook to seed MarkoTM
 */
export function useSeedMarkoTM() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => lawFirmsService.seedMarkoTM(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LAW_FIRMS_KEY });
    }
  });
}

/**
 * Hook to upload an agreement
 */
export function useUploadAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ firmId, data }) => lawFirmsService.uploadAgreement(firmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LAW_FIRMS_KEY });
    }
  });
}

/**
 * Hook to remove an agreement
 */
export function useRemoveAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ firmId, agreementId }) => lawFirmsService.removeAgreement(firmId, agreementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LAW_FIRMS_KEY });
    }
  });
}

export default {
  useCurrentFirm,
  useLawFirms,
  useExternalFirms,
  useCreateFirm,
  useUpdateFirm,
  useSeedMarkoTM,
  useUploadAgreement,
  useRemoveAgreement
};
